import { Platform } from 'react-native';
import RNFS from 'react-native-fs';

/**
 * Compress an image file
 * @param {string} fileUri - URI of the image file (data URL on web, file path on native)
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1920)
 * @param {number} options.maxHeight - Maximum height (default: 1920)
 * @param {number} options.quality - Quality 0-1 (default: 0.8)
 * @param {string} options.outputFormat - Output format: 'jpeg' or 'png' (default: 'jpeg')
 * @returns {Promise<{success: boolean, data?: string, msg?: string}>}
 */
export const compressImage = async (fileUri, options = {}) => {
  try {
    const {
      maxWidth = 1920,
      maxHeight = 1920,
      quality = 0.8,
      outputFormat = 'jpeg'
    } = options;

    if (Platform.OS === 'web') {
      return await compressImageWeb(fileUri, { maxWidth, maxHeight, quality, outputFormat });
    } else {
      return await compressImageNative(fileUri, { maxWidth, maxHeight, quality, outputFormat });
    }
  } catch (error) {
    return { success: false, msg: error.message || 'Image compression failed' };
  }
};

/**
 * Compress image on web using Canvas API
 */
const compressImageWeb = async (fileUri, options) => {
  const { maxWidth, maxHeight, quality, outputFormat } = options;

  return new Promise((resolve) => {
    try {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Use better image smoothing for quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          const mimeType = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve({ success: false, msg: 'Failed to compress image' });
                return;
              }

              // Convert blob to data URL
              const reader = new FileReader();
              reader.onloadend = () => {
                const compressedDataUrl = reader.result;
                resolve({ success: true, data: compressedDataUrl });
              };
              reader.onerror = () => {
                resolve({ success: false, msg: 'Failed to read compressed image' });
              };
              reader.readAsDataURL(blob);
            },
            mimeType,
            quality
          );
        } catch (error) {
          resolve({ success: false, msg: error.message || 'Image compression error' });
        }
      };

      img.onerror = () => {
        resolve({ success: false, msg: 'Failed to load image for compression' });
      };

      // Load image from data URL or URL
      img.src = fileUri;
    } catch (error) {
      resolve({ success: false, msg: error.message || 'Image compression initialization failed' });
    }
  });
};

/**
 * Compress image on native using react-native-image-picker quality
 * Note: Native compression is handled by react-native-image-picker's quality option
 * This function mainly handles file path conversion if needed
 */
const compressImageNative = async (fileUri, options) => {
  try {
    // On native, compression is typically handled by react-native-image-picker
    // with the quality option. If the file is already compressed, we just return it.
    // If we need to recompress, we would need react-native-image-resizer library.
    
    // For now, verify file exists and return the URI
    // The actual compression should be done at picker level with quality option
    const fileExists = await RNFS.exists(fileUri);
    if (!fileExists) {
      return { success: false, msg: 'File does not exist' };
    }

    // Return original URI - compression should be done at picker level
    // If additional compression is needed, consider adding react-native-image-resizer
    return { success: true, data: fileUri };
  } catch (error) {
    return { success: false, msg: error.message || 'Native image compression failed' };
  }
};

/**
 * Compress a video file
 * @param {string} fileUri - URI of the video file
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1920)
 * @param {number} options.maxHeight - Maximum height (default: 1080)
 * @param {number} options.bitrate - Target bitrate in bits per second (default: 2000000 = 2Mbps)
 * @returns {Promise<{success: boolean, data?: string, msg?: string}>}
 */
export const compressVideo = async (fileUri, options = {}) => {
  try {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      bitrate = 2000000 // 2 Mbps
    } = options;

    if (Platform.OS === 'web') {
      return await compressVideoWeb(fileUri, { maxWidth, maxHeight, bitrate });
    } else {
      return await compressVideoNative(fileUri, { maxWidth, maxHeight, bitrate });
    }
  } catch (error) {
    return { success: false, msg: error.message || 'Video compression failed' };
  }
};

/**
 * Compress video on web using MediaRecorder API
 * This reduces video resolution and bitrate to compress the file
 * For very large videos (>100MB), returns original to avoid long processing times
 */
const compressVideoWeb = async (fileUri, options) => {
  const { maxWidth, maxHeight, bitrate } = options;

  return new Promise((resolve) => {
    let video = null;
    let mediaRecorder = null;
    let timeoutId = null;
    let animationFrameId = null;
    let compressionTimeout = null;
    let hasResolved = false;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (compressionTimeout) {
        clearTimeout(compressionTimeout);
        compressionTimeout = null;
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
      if (video) {
        try {
          video.pause();
          video.src = '';
          video.load();
          if (video.parentNode) {
            video.parentNode.removeChild(video);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        video = null;
      }
    };

    const safeResolve = (result) => {
      if (hasResolved) return;
      hasResolved = true;
      cleanup();
      resolve(result);
    };

    // Overall timeout: if compression takes more than 60 seconds, return original
    compressionTimeout = setTimeout(() => {
      safeResolve({ success: true, data: fileUri, msg: 'Compression timeout - using original file' });
    }, 60000); // 60 seconds max

    try {
      video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true; // Muted for processing
      video.playsInline = true;
      video.style.position = 'fixed';
      video.style.top = '-9999px';
      video.style.left = '-9999px';
      video.style.width = '1px';
      video.style.height = '1px';
      document.body.appendChild(video);

      video.onloadedmetadata = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let width = video.videoWidth;
          let height = video.videoHeight;
          const duration = video.duration || 0;

          // If video is already smaller than max dimensions, skip compression
          if (width <= maxWidth && height <= maxHeight) {
            safeResolve({ success: true, data: fileUri });
            return;
          }

          // For very long videos (>5 minutes), skip compression to avoid long processing
          if (duration > 300) {
            safeResolve({ success: true, data: fileUri, msg: 'Video too long - using original file' });
            return;
          }

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }

          // Set video dimensions
          video.width = width;
          video.height = height;
          video.style.width = `${width}px`;
          video.style.height = `${height}px`;

          // Create canvas to capture video stream
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          // Set up MediaRecorder with compression settings
          const stream = canvas.captureStream(30); // 30 fps
          
          // Determine best MIME type supported
          let mimeType = 'video/webm;codecs=vp9';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'video/webm;codecs=vp8';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = 'video/webm';
              if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = 'video/mp4';
              }
            }
          }

          const mediaRecorderOptions = {
            mimeType: mimeType,
            videoBitsPerSecond: bitrate
          };

          // Check if MediaRecorder supports the options
          if (!MediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType)) {
            // Fallback: return original if MediaRecorder doesn't support compression
            safeResolve({ success: true, data: fileUri });
            return;
          }

          try {
            mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
          } catch (recorderError) {
            safeResolve({ success: true, data: fileUri, msg: 'MediaRecorder initialization failed' });
            return;
          }

          const chunks = [];
          let recordingStarted = false;

          mediaRecorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              chunks.push(event.data);
            }
          };

          mediaRecorder.onstop = () => {
            try {
              if (chunks.length === 0) {
                safeResolve({ success: true, data: fileUri, msg: 'No video data captured' });
                return;
              }

              const blob = new Blob(chunks, { type: mimeType });
              
              // Convert blob to data URL
              const reader = new FileReader();
              reader.onloadend = () => {
                const compressedDataUrl = reader.result;
                safeResolve({ success: true, data: compressedDataUrl });
              };
              reader.onerror = () => {
                safeResolve({ success: true, data: fileUri, msg: 'Failed to read compressed video' });
              };
              reader.readAsDataURL(blob);
            } catch (error) {
              safeResolve({ success: true, data: fileUri, msg: error.message || 'Failed to create compressed video blob' });
            }
          };

          mediaRecorder.onerror = (error) => {
            safeResolve({ success: true, data: fileUri, msg: error.message || 'Video compression error' });
          };

          // Start recording when video starts playing
          const startRecording = () => {
            if (!recordingStarted && mediaRecorder && mediaRecorder.state === 'inactive') {
              try {
                recordingStarted = true;
                mediaRecorder.start(100); // Collect data every 100ms
              } catch (e) {
                safeResolve({ success: true, data: fileUri, msg: 'Failed to start recording' });
              }
            }
          };

          // Draw video frames to canvas
          const drawFrame = () => {
            if (!video || video.ended || (video.paused && recordingStarted)) {
              if (mediaRecorder && mediaRecorder.state === 'recording') {
                try {
                  mediaRecorder.stop();
                } catch (e) {
                  // Ignore stop errors
                }
              }
              return;
            }
            
            try {
              // Draw video frame to canvas
              ctx.drawImage(video, 0, 0, width, height);
              
              // Continue drawing frames
              if (mediaRecorder && mediaRecorder.state === 'recording') {
                animationFrameId = requestAnimationFrame(drawFrame);
              }
            } catch (error) {
              // If drawing fails, stop recording
              if (mediaRecorder && mediaRecorder.state === 'recording') {
                try {
                  mediaRecorder.stop();
                } catch (e) {
                  // Ignore stop errors
                }
              }
            }
          };

          video.onplay = () => {
            startRecording();
            drawFrame();
          };

          video.onended = () => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
              try {
                mediaRecorder.stop();
              } catch (e) {
                // Ignore stop errors
              }
            }
          };

          video.onerror = (error) => {
            safeResolve({ success: true, data: fileUri, msg: 'Failed to load video for compression' });
          };

          // Load and play video
          video.currentTime = 0;
          video.src = fileUri;
          
          // Auto-play to start compression (muted, so no audio issues)
          video.play().catch((error) => {
            // If autoplay fails, return original
            safeResolve({ success: true, data: fileUri, msg: 'Autoplay failed' });
          });

        } catch (error) {
          resolve({ success: false, msg: error.message || 'Video compression initialization failed' });
        }
      };

      video.onerror = () => {
        safeResolve({ success: true, data: fileUri, msg: 'Failed to load video metadata' });
      };

      // Load video metadata
      video.src = fileUri;
    } catch (error) {
      safeResolve({ success: true, data: fileUri, msg: error.message || 'Compression initialization failed' });
    }
  });
};

/**
 * Compress video on native
 * Note: react-native-image-picker may apply some compression based on quality settings
 * For more advanced compression, consider using react-native-video-processing
 * For now, we rely on the picker's quality settings and return the file
 */
const compressVideoNative = async (fileUri, options) => {
  try {
    // On native, video compression is typically handled by:
    // 1. react-native-image-picker quality settings (if supported)
    // 2. Additional libraries like react-native-video-processing or react-native-ffmpeg
    
    // Verify file exists
    const fileExists = await RNFS.exists(fileUri);
    if (!fileExists) {
      return { success: false, msg: 'File does not exist' };
    }

    // For now, return the original URI
    // The compression should ideally be done at picker level with quality settings
    // react-native-image-picker may apply some compression based on the quality option
    // For more advanced compression, you would need to add a library like:
    // - react-native-video-processing
    // - react-native-ffmpeg
    // - Or implement a backend compression service
    
    return { success: true, data: fileUri };
  } catch (error) {
    return { success: false, msg: error.message || 'Native video compression failed' };
  }
};

